<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <picture class="a-picture" :class="pictureClass" v-bind="$attrs">
        <template v-if="Boolean(background)">
            <source v-for="(source, i) in backgroundSources" :key="'bg-' + i" v-bind="source" />
        </template>

        <template v-else-if="!isExternalLink">
            <source v-for="(source, i) in contentSources" :key="'ct-' + i" v-bind="source" />
        </template>

        <img
            ref="image"
            class="a-picture__img"
            :class="imageClass"
            :alt="altText"
            :style="{ objectFit, objectPosition: position }"
            :src="fallbackLink"
            :loading="isEager ? 'eager' : 'lazy'"
            :fetchpriority="isEager ? 'high' : null"
            :decoding="isEager ? 'sync' : 'async'"
        />
    </picture>
</template>

<script setup>
import {
    computed,
    ref,
    onMounted,
    onBeforeUnmount,
    onServerPrefetch,
} from 'vue';
import { useStore } from 'vuex';
import { useBreakpoint } from '@core/composables/useBreakpoint';
import { useSsrEngineContext } from '@core/composables/useSsrEngineContext';
import commonUtils from '@utils/common';
import { TYPES } from './constants.js';
import validateFit from './validateFit.js';

const props = defineProps({
    link: { type: String, default: '' },
    alt: { type: String, default: 'Acronis' },
    isShadow: { type: Boolean, default: false },
    isEager: { type: Boolean, default: false },
    fit: {
        type: [String, Object],
        default: undefined,
        validator: validateFit,
    },
    position: { type: String, default: 'center' },
    isBackground: { type: Boolean, default: false },
    background: { type: Object, default: undefined },
    size: {
        type: String,
        default: undefined,
        validator: (v) => v === undefined || TYPES.includes(v),
    },
    persistOriginal: { type: Boolean, default: false },
    observeResize: { type: Boolean, default: false },
});

const emit = defineEmits(['imageLoaded', 'imageResized']);

const store = useStore();
const { isMobile, isTablet } = useBreakpoint();

const $config = computed(() => store.getters['config/$config']);
const baseURL = computed(() => $config.value.env.HEAD_SITE_MAIN_PUBLIC_BASE_URL_STORAGE);
const supportedExtensions = ['webp', 'png'];

const pictureClass = computed(() => ({
    'a-picture_is-background': props.isBackground,
}));
const imageClass = computed(() => ({
    'a-picture__img_shadow': props.isShadow,
}));

const altText = computed(() => commonUtils.altText(props.alt));

const isExternalLink = computed(
    () =>
        props.link.startsWith('http:') ||
        props.link.startsWith('https:') ||
        props.link.startsWith('//'),
);

const device = computed(() => {
    if (isMobile.value) return 'mobile';
    if (isTablet.value) return 'tablet';
    return 'desktop';
});

const buildImageLink = (template, filename = '', extension = 'png') => {
    return filename.startsWith('@') || filename.match(/^[0-9a-f]{32}$/i)
        ? `${baseURL.value}/images/${template}/${filename.replace('@', '')}.${extension}`
        : `${filename}${template.endsWith('@2x') ? '@2' : ''}.${extension}`;
};

const getBackgroundMedia = (name) => {
    switch (name) {
        case 'mobile':
            return '(max-width: 767px)';
        case 'tablet':
            return '(min-width: 768px) and (max-width: 1023px)';
        case 'desktop':
            return '(min-width: 1024px)';
        default:
            return '(max-width: 767px)';
    }
};

const getMIMEType = (ext) => {
    switch (ext) {
        case 'webp':
            return 'image/webp';
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        default:
            return 'application/octet-stream';
    }
};

const fallbackLink = computed(() => {
    if (props.background) {
        const hash = props.background[device.value];
        return buildImageLink('background', hash, 'png');
    }

    if (props.link) {
        if (isExternalLink.value) return props.link;
        const templateType = props.size || 'content';
        return buildImageLink(templateType, props.link, 'png');
    }

    return '';
});

const objectFit = computed(() => {
    if (typeof props.fit !== 'object') return props.fit;
    return props.fit?.[device.value] || props.fit?.default || 'fill';
});

const buildBackgroundSource = (name, value, ext) => {
    const template1x = props.persistOriginal ? 'background@2x' : 'background';

    const template2x = 'background@2x';
    const descriptor = props.persistOriginal ? '' : '2x';

    return {
        media: getBackgroundMedia(name),
        srcset: [
            buildImageLink(template1x, value, ext),
            `${buildImageLink(template2x, value, ext)} ${descriptor}`.trim(),
        ].join(', '),
        type: getMIMEType(ext),
    };
};

const buildContentSource = (ext) => {
    const templateType = props.size || 'content';
    const template1x = props.persistOriginal ? `${templateType}@2x` : templateType;

    const template2x = `${templateType}@2x`;
    const descriptor = props.persistOriginal ? '' : '2x';

    return {
        srcset: [
            buildImageLink(template1x, props.link, ext),
            `${buildImageLink(template2x, props.link, ext)} ${descriptor}`.trim(),
        ].join(', '),
        type: getMIMEType(ext),
    };
};

const backgroundSources = computed(() => {
    if (!props.background) return [];

    return Object.entries(props.background).flatMap(([name, value]) =>
        supportedExtensions.map((ext) => buildBackgroundSource(name, value, ext)),
    );
});

const contentSources = computed(() => {
    if (props.background || isExternalLink.value) return [];

    return supportedExtensions.map((ext) => buildContentSource(ext));
});

onServerPrefetch(() => {
    if (!props.isEager) return;
    const ssr = useSsrEngineContext();

    const candidates = props.background
        ? backgroundSources.value.filter((s) => s.media?.includes('1024'))
        : contentSources.value;

    const chosen = candidates.find((s) => s.type === 'image/webp') ||
        candidates.find((s) => s.type === 'image/png') ||
        candidates[0];

    if (!chosen?.srcset) return;
    const parts = chosen.srcset.split(',').map((p) => p.trim());
    const retina = parts.find((p) => p.includes('2x'));
    const href = retina ? retina.replace(/\s+2x$/, '') : parts[0];
    if (!href) return;

    const hint = {
        tag: 'link',
        rel: 'preload',
        as: 'image',
        href,
        fetchpriority: 'high',
        imagesrcset: chosen.srcset,
        imagesizes: '100vw',
        media: chosen.media,
    };
    ssr?.pushHead(hint);
});

const image = ref(null);

const handleResize = () => {
    if (!image.value) return;
    emit('imageResized', image.value);
};

onMounted(() => {
    if (!image.value) return;

    image.value.onload = () => {
        emit('imageLoaded', image.value);
    };

    if (props.observeResize) {
        window.addEventListener('resize', handleResize);
    }
});

onBeforeUnmount(() => {
    if (!image.value) return;

    image.value.onload = null;
    window.removeEventListener('resize', handleResize);
});
</script>

<style lang="pcss" scoped>
.a-picture {
    display: block;

    &_is-background {
        .a-picture__img {
            position: absolute;
            width: 100%;
            height: 100%;
            bottom: 0;
        }
    }

    &__img {
        display: block;
        max-width: 100%;
        max-height: 100%;
        margin: 0 auto;

        &_shadow {
            box-shadow: var(--av-shadow-regular);
        }
    }
}
</style>
